// src/pages/visa/SkilledMigration.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Tilt from "react-parallax-tilt";
import {
  FaGlobeAsia,
  FaMapMarkerAlt,
  FaClipboardList,
  FaUniversity,
  FaUserTie,
  FaCalculator,
  FaQuestionCircle,
  FaExternalLinkAlt,
  FaArrowRight,
  FaCheckCircle,
  FaInfoCircle,
  FaListUl,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

const STREAMS = [
  {
    key: "189",
    title: "Skilled Independent (189)",
    bullets: [
      "Points‑tested, invitation‑only",
      "No state/territory sponsorship",
      "Permanent residence pathway",
    ],
  },
  {
    key: "190",
    title: "Skilled Nominated (190)",
    bullets: [
      "State/territory nomination required",
      "Additional points for nomination",
      "Permanent residence pathway",
    ],
  },
  {
    key: "491",
    title: "Skilled Work Regional (491)",
    bullets: [
      "Regional nomination/sponsorship",
      "Provisional visa with PR pathway",
      "Conditions and residence requirements",
    ],
  },
];

const FAQ = [
  { q: "What is a competitive points score?", a: "It varies by occupation and round. Some states set their own thresholds for 190/491 nomination." },
  { q: "Do I need a skills assessment?", a: "Most skilled visas require a positive skills assessment by the relevant assessing authority." },
  { q: "How do state nominations work?", a: "States/territories publish criteria and target lists. You may need to live/work/study there and submit a registration of interest." },
  { q: "Does the estimator guarantee eligibility?", a: "No. It’s a simple guide only; official points are assessed at invitation/lodgement under current rules." },
];

export default function SkilledMigration() {
  useEffect(() => { document.title = "Skilled Migration (189 / 190 / 491) | Glide Education"; }, []);

  const [stream, setStream] = useState("189");

  // Simple illustrative points estimator
  const [age, setAge] = useState(30);            // 0..30
  const [english, setEnglish] = useState(10);    // 0..20
  const [ausStudy, setAusStudy] = useState(false); // +5
  const [professionalYear, setProfessionalYear] = useState(false); // +5
  const [partner, setPartner] = useState(0);     // 0..10
  const [education, setEducation] = useState(15); // 10/15/20
  const [experienceAus, setExperienceAus] = useState(5); // 0..20
  const [experienceOverseas, setExperienceOverseas] = useState(0); // 0..15
  const [stateNomination, setStateNomination] = useState(0); // 0/5/15

  const totalPoints = useMemo(
    () =>
      age +
      english +
      education +
      experienceAus +
      experienceOverseas +
      (ausStudy ? 5 : 0) +
      (professionalYear ? 5 : 0) +
      partner +
      stateNomination,
    [age, english, education, experienceAus, experienceOverseas, ausStudy, professionalYear, partner, stateNomination]
  );

  const ready = useMemo(() => totalPoints >= 65, [totalPoints]);

  return (
    <motion.main initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="min-h-screen bg-white">
      {/* HERO */}
      <div className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-300/25 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-indigo-300/25 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-6xl px-6 pt-12 pb-10 md:pb-14">
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" className="text-3xl md:text-5xl font-extrabold tracking-tight text-blue-900">
            Skilled Migration — 189 / 190 / 491
          </motion.h1>
          <motion.p variants={fadeUp} custom={1} initial="hidden" animate="show" className="mt-3 max-w-3xl text-gray-700">
            Explore points‑tested pathways to live and work in Australia. Competitiveness varies by occupation, state and invitation round.
          </motion.p>

          {/* Actions */}
          <motion.div variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-6 flex flex-wrap gap-3">
            <a
              href="https://immi.homeaffairs.gov.au/visas/working-in-australia/skillselect"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-semibold text-white shadow hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Official SkillSelect <FaExternalLinkAlt className="opacity-90" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-blue-600 bg-white px-5 py-2.5 font-semibold text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              Speak to a Consultant <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* BODY GRID */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-6 pb-16 md:grid-cols-[1fr_320px]">
        {/* LEFT */}
        <div>
          {/* Streams */}
          <section id="streams" className="scroll-mt-24">
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-2xl font-bold text-blue-800">
              Choose your stream
            </motion.h2>

            <div className="mt-4 flex flex-wrap gap-3">
              {STREAMS.map((s) => (
                <button
                  type="button"
                  key={s.key}
                  onClick={() => setStream(s.key)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    stream === s.key ? "bg-blue-600 text-white shadow" : "bg-white text-blue-700 border border-blue-200 hover:bg-blue-50"
                  }`}
                  aria-pressed={stream === s.key}
                >
                  {s.title}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {STREAMS.filter((s) => s.key === stream).map((s) => (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                  className="mt-4 grid gap-4 md:grid-cols-3"
                >
                  {s.bullets.map((b) => (
                    <Tilt
                      key={b}
                      tiltMaxAngleX={8}
                      tiltMaxAngleY={8}
                      scale={1.02}
                      transitionSpeed={400}
                      glareEnable
                      glareMaxOpacity={0.12}
                      glareColor="#60a5fa"
                      className="rounded-2xl border bg-white/80 p-4 shadow-sm backdrop-blur"
                    >
                      <div className="flex items-center gap-3 text-blue-900">
                        <span className="text-lg">
                          {s.key === "190" ? <FaMapMarkerAlt /> : s.key === "189" ? <FaGlobeAsia /> : <FaListUl />}
                        </span>
                        <div className="font-semibold">{b}</div>
                      </div>
                    </Tilt>
                  ))}
                </motion.div>
              ))}
            </AnimatePresence>
          </section>

          {/* Points estimator */}
          <section id="points" className="mt-10 scroll-mt-24">
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-2xl font-bold text-blue-800">
              Quick points estimator (guide only)
            </motion.h2>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Select
                label="Age"
                value={age}
                onChange={setAge}
                options={[
                  { label: "Under 25 (25 pts)", value: 25 },
                  { label: "25–32 (30 pts)", value: 30 },
                  { label: "33–39 (25 pts)", value: 25 },
                  { label: "40–44 (15 pts)", value: 15 },
                  { label: "45+ (0 pts)", value: 0 },
                ]}
              />

              <Select
                label="English"
                value={english}
                onChange={setEnglish}
                options={[
                  { label: "Competent (0 pts)", value: 0 },
                  { label: "Proficient (10 pts)", value: 10 },
                  { label: "Superior (20 pts)", value: 20 },
                ]}
              />

              <Select
                label="Highest education"
                value={education}
                onChange={setEducation}
                options={[
                  { label: "Diploma/Trade (10 pts)", value: 10 },
                  { label: "Bachelor (15 pts)", value: 15 },
                  { label: "Masters/PhD (20 pts)", value: 20 },
                ]}
              />

              <Select
                label="Australian skilled experience"
                value={experienceAus}
                onChange={setExperienceAus}
                options={[
                  { label: "None (0)", value: 0 },
                  { label: "1–2 yrs (5)", value: 5 },
                  { label: "3–4 yrs (10)", value: 10 },
                  { label: "5–7 yrs (15)", value: 15 },
                  { label: "8+ yrs (20)", value: 20 },
                ]}
              />

              <Select
                label="Overseas skilled experience"
                value={experienceOverseas}
                onChange={setExperienceOverseas}
                options={[
                  { label: "None (0)", value: 0 },
                  { label: "3–4 yrs (5)", value: 5 },
                  { label: "5–7 yrs (10)", value: 10 },
                  { label: "8+ yrs (15)", value: 15 },
                ]}
              />

              <Toggle label="Australian study requirement (5 pts)" checked={ausStudy} onChange={setAusStudy} icon={<FaUniversity />} />
              <Toggle label="Professional Year in Australia (5 pts)" checked={professionalYear} onChange={setProfessionalYear} icon={<FaUserTie />} />

              <Select
                label="Partner"
                value={partner}
                onChange={setPartner}
                options={[
                  { label: "No partner / partner Aus citizen (10 pts)", value: 10 },
                  { label: "Partner competent English (5 pts)", value: 5 },
                  { label: "Partner skills assessed (10 pts)", value: 10 },
                ]}
              />

              <Select
                label="Nomination / sponsorship"
                value={stateNomination}
                onChange={setStateNomination}
                options={[
                  { label: "None (0)", value: 0 },
                  { label: "190 state nomination (5 pts)", value: 5 },
                  { label: "491 regional nomination/sponsor (15 pts)", value: 15 },
                ]}
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-xl border bg-white/80 px-4 py-2 text-blue-900 shadow-sm"><FaCalculator /> Estimated points: <strong>{totalPoints}</strong></div>
              <div className={`rounded-xl px-3 py-2 text-sm ${ready ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-800"}`}>
                {ready ? (
                  <span className="inline-flex items-center gap-2"><FaCheckCircle /> You’re broadly in range. We’ll verify your occupation and evidence.</span>
                ) : (
                  <span className="inline-flex items-center gap-2"><FaInfoCircle className="text-blue-600" /> Below 65. Consider English, experience, or nomination pathways.</span>
                )}
              </div>
            </div>

            <Note className="mt-3">This estimator is a guide only and not legal advice. Official points and criteria are assessed under current legislation and policy.</Note>
          </section>

          {/* Steps */}
          <section id="steps" className="mt-10 scroll-mt-24">
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-2xl font-bold text-blue-800">
              How we assist — end‑to‑end
            </motion.h2>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <Step title="Check occupation & skills" body="Confirm ANZSCO alignment and arrange skills assessment with the relevant authority." />
              <Step title="Points & strategy" body="Improve competitiveness via English, experience, PY, or nomination paths (190/491)." />
              <Step title="EOI & nomination" body="Submit SkillSelect EOI and state ROI where relevant, then prepare invitation stage docs." />
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="mt-10 scroll-mt-24">
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-2xl font-bold text-blue-800">
              FAQs
            </motion.h2>
            <div className="mt-3 space-y-3">
              {FAQ.map((f) => (
                <Accordion key={f.q} title={f.q} body={f.a} />
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="mt-12">
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-[1px]">
              <div className="rounded-2xl bg-white/90 px-6 py-8 backdrop-blur">
                <div className="grid items-center gap-6 md:grid-cols-2">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900">Let’s map your skilled pathway.</h3>
                    <p className="mt-1 text-gray-600">We’ll validate your points, skills assessment route, and nomination options, then prepare a strong application pack.</p>
                  </div>
                  <div className="flex md:justify-end">
                    <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow hover:bg-blue-500">
                      Speak to a Consultant <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </section>
        </div>

        {/* RIGHT: sticky quick nav */}
        <aside className="top-24 hidden md:block">
          <div className="sticky top-24 rounded-2xl border bg-white/80 p-5 shadow-sm backdrop-blur">
            <div className="text-sm font-semibold text-blue-900">Quick links</div>
            <nav className="mt-3">
              <ul className="space-y-2 text-sm">
                <li><a className="text-blue-700 hover:underline" href="#streams">Streams</a></li>
                <li><a className="text-blue-700 hover:underline" href="#points">Points estimator</a></li>
                <li><a className="text-blue-700 hover:underline" href="#steps">How we assist</a></li>
                <li><a className="text-blue-700 hover:underline" href="#faq">FAQs</a></li>
              </ul>
            </nav>
            <hr className="my-4 border-blue-100" />
            <a href="https://immi.homeaffairs.gov.au/visas/working-in-australia/skillselect" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-800 hover:bg-blue-100">
              SkillSelect guidance <FaExternalLinkAlt className="opacity-80" />
            </a>
          </div>
        </aside>
      </div>
    </motion.main>
  );
}

/* ---------- Bits ---------- */
function Note({ children, className = "" }) {
  return <div className={`my-4 rounded-md border-l-4 border-blue-600 bg-blue-50 p-4 text-sm ${className}`}>{children}</div>;
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="flex flex-col gap-1 rounded-xl border bg-white/80 p-4 text-sm text-gray-800 shadow-sm backdrop-blur">
      <span className="font-medium text-blue-900">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="rounded-md border border-blue-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        {options.map((o) => (
          <option key={o.label} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function Toggle({ label, checked, onChange, icon }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border bg-white/80 p-4 text-sm text-gray-800 shadow-sm backdrop-blur">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-400" />
      <span className="flex items-start gap-2"><span className="mt-0.5 text-blue-600">{icon}</span> {label}</span>
    </label>
  );
}

function Step({ title, body }) {
  return (
    <Tilt tiltMaxAngleX={8} tiltMaxAngleY={8} scale={1.02} transitionSpeed={400} glareEnable glareMaxOpacity={0.12} glareColor="#60a5fa" className="rounded-2xl border bg-white/80 p-5 shadow-sm backdrop-blur">
      <div className="flex items-center gap-3 text-blue-900">
        <span className="text-lg"><FaClipboardList /></span>
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="mt-1 text-sm text-gray-700">{body}</p>
    </Tilt>
  );
}

function Accordion({ title, body }) {
  const [open, setOpen] = useState(false);
  const id = useMemo(() => `acc-${title.toLowerCase().replace(/\s+/g, "-")}`, [title]);

  return (
    <div className="rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-lg px-1"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        id={id}
      >
        <span className="font-medium text-blue-900 flex items-center gap-2">
          <FaQuestionCircle className="text-blue-600" /> {title}
        </span>
        <span className="text-blue-700" aria-hidden="true">{open ? "–" : "+"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`${id}-panel`}
            role="region"
            aria-labelledby={id}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <p className="mt-2 text-sm leading-relaxed text-gray-700">{body}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
